import { useSelector } from 'react-redux';
import React, { useLayoutEffect, useRef } from 'react';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { clsx } from 'clsx';
import { useRouteParams } from '@/context/routeParamsContext';
import {
  useCommonContent,
  useOneCmsLocaleOverviewBody,
  useOneCmsLocaleOverviewDisclaimer,
} from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import {
  TclMigrationFeatures,
  useTCLFeatureFlags,
} from '@/hooks/featureFlagHooks';
import { RouteParams } from '@/types';
import { useAnalyticsContext } from '@/context/analyticsContext';
import Flourish from '../icons/Flourish';
import styles from './CenturionLoungeNetworkSection.module.scss';
import LFLink from '../common/LFLink';

const clampProgress = (value: number) => Math.min(1, Math.max(0, value));
const parallaxStartOffset = 25; // The offset from the top of the viewport where the parallax effect starts
const parallaxRangeRatio = 1; // The range over which the parallax effect occurs
const parallaxProgressProperty = '--parallax-progress';

const CenturionLoungeNetworkSection = () => {
  const { customTrack } = useAnalyticsContext();
  const { product } = useRouteParams() as RouteParams;
  const isDeltaReserve = product === 'delta-reserve';
  const cmsImgBaseUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const imagesContainerRef = useRef<HTMLDivElement | null>(null);
  const { partners } = useCommonContent();
  const overviewBody = useOneCmsLocaleOverviewBody();
  const disclaimer = useOneCmsLocaleOverviewDisclaimer();
  const isEligibleCNMarket = useTCLFeatureFlags(
    TclMigrationFeatures.CENTURION_NETWORK_PAGE
  );

  useLayoutEffect(() => {
    const imagesContainer = imagesContainerRef.current;
    if (!imagesContainer || typeof window === 'undefined') return undefined;

    let frameId: number | undefined;
    let lastProgress = -1;

    const applyProgress = (progress: number) => {
      if (progress === lastProgress) return;
      lastProgress = progress;
      imagesContainer.style.setProperty(
        parallaxProgressProperty,
        progress.toString()
      );
    };

    const updateProgress = () => {
      frameId = undefined;
      const { top } = imagesContainer.getBoundingClientRect();
      const parallaxRange = window.innerHeight * parallaxRangeRatio;
      applyProgress(
        clampProgress(
          (window.innerHeight - top - parallaxStartOffset) / parallaxRange
        )
      );
    };

    const handleScroll = () => {
      if (frameId === undefined) {
        frameId = window.requestAnimationFrame(updateProgress);
      }
    };

    const handleResize = () => {
      if (frameId !== undefined) {
        window.cancelAnimationFrame(frameId);
        frameId = undefined;
      }
      lastProgress = -1;
      updateProgress();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });
    updateProgress();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      if (frameId !== undefined) window.cancelAnimationFrame(frameId);
      imagesContainer.style.removeProperty(parallaxProgressProperty);
    };
  }, []);

  return (
    <>
      <h3
        className={clsx(
          { 'tw-mb-6': isDeltaReserve },
          'tw-heading-md-regular tw-mt-10 tw-text-gray-900'
        )}
      >
        {partners?.header}
      </h3>
      {!isDeltaReserve && (
        <>
          <p className={!disclaimer ? 'tw-mb-6' : ''}>{overviewBody}</p>
          {disclaimer && <p className="tw-mb-6">{disclaimer}</p>}
        </>
      )}

      <div className="tw-relative tw-hidden tw-h-100 tw-flex-row tw-gap-10 tw-overflow-hidden tw-rounded-2xl tw-border tw-bg-blue-deep-700 tw-px-6 tw-py-8 sm_tw-flex md_tw-h-95 lg_tw-h-113dot75 lg_tw-px-16">
        {/* Images */}
        <div
          className={clsx('tw-flex lg_tw-w-full', styles.imagesContainer)}
          data-testid="centurion-network-images-container"
          ref={imagesContainerRef}
        >
          <div className={clsx('tw-w-full', styles.imagesLeft)}>
            {/* Image 1 */}
            <div className={clsx('tw-absolute tw-z-10', styles.loungeImage)}>
              {/* large bp */}
              <img
                className="tw-hidden tw-w-full tw-translate-x-45 tw-translate-y-25 tw-transform lg_tw-flex"
                src={`${cmsImgBaseUrl}centurion-network-lounge-large.png`}
                alt="Centurion Lounge"
              />
              {/* medium bp */}
              <img
                className="tw-hidden tw-w-full tw-translate-x-45 tw-translate-y-25 tw-transform md_tw-flex lg_tw-hidden"
                src={`${cmsImgBaseUrl}the-centurion-network-lounge-medium.png`}
                alt="Centurion Lounge"
              />
              {/* small bp */}
              <img
                className="tw-flex tw-w-full md_tw-hidden"
                src={`${cmsImgBaseUrl}the-centurion-network-lounge-small.png`}
                alt="Centurion Lounge"
              />
            </div>
            {/* image 2 */}
            <div
              className={clsx(
                'tw-relative tw-overflow-hidden tw-rounded-2xl',
                styles.foodImage
              )}
            >
              {/* large bp */}
              <img
                className="tw-hidden tw-w-full lg_tw-flex"
                src={`${cmsImgBaseUrl}centurion-network-food-large.png`}
                alt="Centurion Lounge Food"
              />
              {/* medium bp */}
              <img
                className="tw-flex tw-hidden tw-w-full md_tw-flex lg_tw-hidden"
                src={`${cmsImgBaseUrl}the-centurion-network-food-medium.png`}
                alt="Centurion Lounge Food"
              />
              {/* small bp */}
              <img
                className="tw-flex tw-w-full md_tw-hidden"
                src={`${cmsImgBaseUrl}the-centurion-network-food-small.png`}
                alt="Centurion Lounge Food"
              />
            </div>
            {/* image 3 medium bp */}
            <div className={clsx('tw-relative lg_tw-hidden', styles.barImage)}>
              <img
                className="tw-hidden tw-w-full md_tw-flex lg_tw-hidden"
                src={`${cmsImgBaseUrl}the-centurion-network-bar-medium.png`}
                alt="Centurion Lounge Bar"
              />
              <img
                className="tw-w-full md_tw-hidden"
                src={`${cmsImgBaseUrl}the-centurion-network-bar-small.png`}
                alt="Centurion Lounge Bar"
              />
            </div>
          </div>
          {/* Image 3 large bp */}
          <div
            className={clsx(
              'tw-hidden tw-w-full lg_tw-flex',
              styles.imagesRight
            )}
          >
            <div className={clsx('tw-relative', styles.barImage)}>
              <img
                className="tw-w-full"
                src={`${cmsImgBaseUrl}centurion-network-bar-large.png`}
                alt="Centurion Lounge Bar"
              />
            </div>
          </div>
        </div>

        {/* Megaphone content with button */}
        <div className="tw-flex tw-w-full tw-flex-1 tw-flex-col tw-justify-center">
          <h4 className="tw-heading-sm-bold tw-mb-1 tw-text-white">
            {partners?.cnHeader}
          </h4>
          <p className="tw-mb-6 tw-text-white lg_tw-w-88">
            {partners.cnDescription}
          </p>
          {isEligibleCNMarket && (
            <div>
              <LFLink
                className={`${styles.viewDetailsBtn} btn primary btn-primary flex-justify-center dls-white tw-z-10 tw-max-w-300 tw-px-6`}
                to="centurion-network"
                onClickHandler={(event) =>
                  customTrack({
                    event,
                    element: 'The Centurion Lounge Banner>>View Details',
                    detail: 'Click View Details ',
                  })
                }
              >
                {partners.cnCTALabel}
              </LFLink>
            </div>
          )}
        </div>

        {/* Flourish element absolute */}
        <Flourish
          className="tw-absolute tw-bottom-0 tw-right-6"
          style={{
            maskImage: 'linear-gradient(to left, black 0%, transparent 88%)',
          }}
        />
      </div>
    </>
  );
};

export default CenturionLoungeNetworkSection;
